/**
 * A CÉGVÁLASZTÓ A VÁZBAN (#73) — egy horogban, hogy a váz ne ágazzon el miatta.
 *
 * Amit ad: kell-e egyáltalán választó (egy cégnél nincs mit választani), a
 * cégsort, a „mind" sor feliratát, és a regiszter meg a paletta bejegyzését.
 * A váz ezeket csak beteszi a saját listáiba.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Command } from '@/features/palette/commands';
import type { Shortcut } from '@/hooks/useShortcuts';
import type { Me } from '@/lib/api-client';
import { type SelectableCompanies, useSelectableCompanies } from './useSelectableCompanies';

export interface CompanyPickerState {
  /** Van-e mit választani — az ügyfélnél (`single`) nincs. */
  canPick: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  companies: SelectableCompanies;
  /** „Az iroda minden cége", vagy kiosztott hatókörnél „A kiosztott cégeim (n)". */
  allLabel: string;
  /** A `c` — üres, ha nincs mit választani. */
  shortcuts: Shortcut[];
  /** A paletta „Cégváltás" sora — üres, ha nincs mit választani. */
  commands: Command[];
}

/**
 * @param blocked — a 2.6/22 zára: elintézetlen bukással a munkalistát nem
 *   lehet elhagyni, és a cégváltás is elhagyás (a bukott mentés sávja a régi
 *   cég bizonylatáról szól).
 */
export function useCompanyPicker(me: Me | null, blocked: boolean): CompanyPickerState {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const canPick = me !== null && me.companies.kind !== 'single';
  const companies = useSelectableCompanies(canPick);

  const allLabel =
    me?.companies.kind === 'assigned'
      ? `${t('companyFilter.assigned')} (${me.companies.companyIds.length})`
      : t('companyFilter.all');

  const open = useCallback(() => {
    if (canPick && !blocked) setOpen(true);
  }, [canPick, blocked]);
  const close = useCallback(() => setOpen(false), []);

  // A `c` betű, nem módosítós: beviteli mezőben betű marad, a `useShortcuts`
  // ott nem fut le.
  const shortcuts = useMemo<Shortcut[]>(
    () => (canPick ? [{ key: 'c', labelKey: 'shortcut.companyPicker', run: open }] : []),
    [canPick, open]
  );
  const commands = useMemo<Command[]>(
    () =>
      canPick
        ? [{ id: 'company-picker', labelKey: 'palette.companyPicker', hint: 'c', run: open }]
        : [],
    [canPick, open]
  );

  return { canPick, isOpen, open, close, companies, allLabel, shortcuts, commands };
}
